import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  GridItem,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  useToast,
  Checkbox,
} from '@chakra-ui/react';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import {
  CreateMaterialRequest,
  materialsApi,
} from '@texas/api/endpoints/metadata/materialsApi';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { MixedMaterialFields } from './MixedMaterialFields';
import { hasValidMaterialPercentages } from './mixedMaterial';

interface Props extends SharedDisclosureProps {
  onCreated: () => void;
}

const defaultValues: CreateMaterialRequest = {
  name: '',
  description: '',
  articleTextField: '',
  certificationTextField: '',
  displayOrder: 0,
  childMaterials: [],
  lockArticleAfterSync: false,
};

export function CreateMaterial({ onCreated, onClose, isOpen }: Props) {
  const toast = useToast();
  const { t } = useTranslation();
  const { request: createRequest, error } = useApiRequest(materialsApi.create);

  const {
    data: materials,
    refetch: refetchMaterials,
    loading: materialsLoading,
  } = useApiResource(materialsApi.getAll);

  useEffect(() => {
    if (!isOpen) return;
    refetchMaterials();
  }, [isOpen, refetchMaterials]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateMaterialRequest>({
    defaultValues: defaultValues,
  });
  const watchCombinations = watch('childMaterials', []);

  const onSubmit = async (data: CreateMaterialRequest) =>
    await request(
      createRequest,
      [data],
      () => {
        reset(defaultValues);
        toast({
          title: t('general.created'),
          status: 'success',
          isClosable: true,
        });
        onCreated();
      },
      (error: ServerError) => {
        toast({
          title: t('general.createFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  return (
    <Modal
      variant="entityDirectory"
      size="4xl"
      scrollBehavior="inside"
      onClose={() => {
        reset(defaultValues);
        onClose();
      }}
      isOpen={isOpen}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader fontWeight="bold">
          {t('configuration.addNewMaterial')}
        </ModalHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <ModalBody>
            {error && <ErrorDetails error={error} />}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={4}>
              <FormControl isRequired={true} isInvalid={!!errors.name}>
                <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('name', {
                    required: true,
                    maxLength: {
                      value: 35,
                      message: t('errors.maxLength', { count: 35 }),
                    },
                  })}
                  placeholder={t('general.name')}
                />
                <HookFormCounter control={control} name="name" maxLength={35} />
                <ErrorLabel text={errors.name?.message} />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.description}>
                <TexasFormLabel>{t('general.description')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('description', {
                    required: true,
                    maxLength: {
                      value: 35,
                      message: t('errors.maxLength', { count: 35 }),
                    },
                  })}
                  placeholder={t('general.description')}
                />
                <HookFormCounter
                  control={control}
                  name="description"
                  maxLength={35}
                />
                <ErrorLabel text={errors.description?.message} />
              </FormControl>
              <FormControl
                isRequired={true}
                isInvalid={!!errors.articleTextField}
              >
                <TexasFormLabel>
                  {t('configuration.articleTextField')}
                </TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('articleTextField', {
                    required: true,
                    maxLength: {
                      value: 20,
                      message: t('errors.maxLength', { count: 20 }),
                    },
                  })}
                  placeholder={t('configuration.articleTextField')}
                />
                <FormCounter
                  length={watch('articleTextField').length}
                  maxLength={20}
                />
                <ErrorLabel text={errors.articleTextField?.message} />
              </FormControl>
              <FormControl isInvalid={!!errors.certificationTextField}>
                <TexasFormLabel>
                  {t('configuration.certificationTextField')}
                </TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('certificationTextField', {
                    maxLength: {
                      value: 20,
                      message: t('errors.maxLength', { count: 20 }),
                    },
                  })}
                  placeholder={t('configuration.certificationTextField')}
                />
                <ErrorLabel text={errors.certificationTextField?.message} />
                <FormCounter
                  length={watch('certificationTextField').length}
                  maxLength={20}
                />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.displayOrder}>
                <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  {...register('displayOrder', {
                    required: true,
                    valueAsNumber: true,
                  })}
                  placeholder={t('branch.displayOrder')}
                />
                <ErrorLabel text={errors.displayOrder?.message} />
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.lockMtrlAfterSync')}
                </TexasFormLabel>
                <Controller
                  name="lockArticleAfterSync"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Checkbox onChange={onChange} isChecked={value} />
                  )}
                />
              </FormControl>
              <GridItem
                colSpan={{ base: 1, md: 2 }}
                borderTopWidth="1px"
                borderColor="entityDirectory.border"
                pt={4}
              >
                <MixedMaterialFields
                  control={control}
                  register={register}
                  materials={materials}
                  isLoading={materialsLoading}
                />
              </GridItem>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Box w={{ base: 'full', md: '240px' }}>
              <SubmitButton
                disabled={!hasValidMaterialPercentages(watchCombinations)}
                loading={isSubmitting}
              >
                {watchCombinations.length === 0
                  ? t('configuration.createMaterial')
                  : t('configuration.createMixedMaterial')}
              </SubmitButton>
            </Box>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
